import { useFormContext } from 'react-hook-form';
import { useState } from 'react';
import { Banner, Button, Divider, IconName, Stack, Typography } from '@optimus-web/core';
import { lt } from '@mews/framework';
import { TextKey, DataTestId } from '@mews-commander/enums';
import { UnicodeEntity } from '@mews/enums';
import { useRoutingRuleConflictsContext } from '../../../../../contexts/RoutingRuleConflictsContext';
import {
    CompaniesFormFields,
    CompaniesFormValues,
    ItemsAndConditionsSchemaFormValues,
    RatesOptionsNames,
    ServiceFormFields,
} from '../../../../../schemas';
import { RoutingRulesFormStepName } from '../../../../../types';
import { getSelectedCompaniesCount } from '../../../../../utils/companies';
import { useRoutingRulesData } from '../../../../../queries';
import { RoutingRulesFormFields } from '../../../utils';
import { getConflicts } from './getConflicts';
import { ConflictsList } from './ConflictsList';
import { ExistingConflictAccordion } from './ExistingConflictAccordion';

export const ConflictsDialogContent = () => {
    const { conflicts, setConflicts, routingRuleId } = useRoutingRuleConflictsContext();
    const { setValue, watch } = useFormContext();
    const { data } = useRoutingRulesData();
    const [expandedAccordions, setExpandedAccordions] = useState<{ [key: string]: boolean }>({});

    const selectedValues: ItemsAndConditionsSchemaFormValues = watch(RoutingRulesFormStepName.ItemsAndConditions);
    const { services: selectedServices, companies: selectedCompanies } = selectedValues;

    if (conflicts == null) {
        return (
            <Banner
                variant="success"
                primaryText={lt(TextKey.RoutingRulesFormConflictsSuccessBannerTitle)}
                secondaryText={lt(TextKey.RoutingRulesFormConflictsSuccessBannerDescription)}
            />
        );
    }

    const { services, companies, travelAgencies, rates, prepayment } = conflicts.allConflictingValues;

    const updateConflicts = () => {
        const conflictsData = getConflicts(selectedValues, data, routingRuleId);
        setConflicts(conflictsData);
    };

    const handleDeselectService = (id: string) => {
        setValue(RoutingRulesFormFields.services(), {
            ...selectedServices,
            [id]: {
                ...selectedServices[id],
                [ServiceFormFields.Enabled]: false,
            },
        });
        updateConflicts();
    };

    const handleDeselectAllServices = () => {
        const conflictedServiceIds = conflicts.allConflictingValues.services.map((service) => service.id);
        const updatedServices = { ...selectedServices };

        Object.keys(updatedServices).forEach((id) => {
            if (conflictedServiceIds.includes(id)) {
                updatedServices[id] = {
                    ...updatedServices[id],
                    [ServiceFormFields.Enabled]: false,
                };
            }
        });

        setValue(RoutingRulesFormFields.services(), updatedServices);
        updateConflicts();
    };

    const handleDeselectRate = (serviceId: string) => (id: string) => {
        const currentSelectedRates = selectedValues.services[serviceId].serviceRates?.selectedRates || [];
        const updatedSelectedRates = currentSelectedRates.filter((rateId: string) => rateId !== id);

        if (updatedSelectedRates.length === 0) {
            setValue(RoutingRulesFormFields.serviceRatesType(serviceId), RatesOptionsNames.All);
        } else {
            setValue(RoutingRulesFormFields.serviceSelectedRates(serviceId), updatedSelectedRates);
        }
        updateConflicts();
    };

    const handleDeselectAllRates = (serviceId: string) => {
        setValue(RoutingRulesFormFields.serviceRatesType(serviceId), RatesOptionsNames.All);
        updateConflicts();
    };

    const handleDeselectCompany =
        (linkType: CompaniesFormFields.LinkAsCompany | CompaniesFormFields.LinkAsTravelAgency) => (id: string) => {
            const updatedCompanies = {
                ...selectedCompanies,
                [id]: {
                    ...(selectedCompanies as CompaniesFormValues)[id],
                    [linkType]: false,
                },
            };
            setValue(RoutingRulesFormFields.companies(), updatedCompanies);
            if (getSelectedCompaniesCount(updatedCompanies) === 0) {
                setValue(RoutingRulesFormFields.companiesEnabled(), false);
            }
            updateConflicts();
        };

    const handleDeselectAllCompanies = (
        linkType: CompaniesFormFields.LinkAsCompany | CompaniesFormFields.LinkAsTravelAgency
    ) => {
        const conflictedCompanyIds =
            linkType === CompaniesFormFields.LinkAsCompany
                ? conflicts.allConflictingValues.companies.map((company) => company.id)
                : conflicts.allConflictingValues.travelAgencies.map((agency) => agency.id);

        const updatedCompanies = { ...selectedCompanies };

        Object.keys(updatedCompanies).forEach((id) => {
            if (conflictedCompanyIds.includes(id)) {
                updatedCompanies[id] = {
                    ...updatedCompanies[id],
                    [linkType]: false,
                };
            }
        });

        setValue(RoutingRulesFormFields.companies(), updatedCompanies);
        if (getSelectedCompaniesCount(updatedCompanies) === 0) {
            setValue(RoutingRulesFormFields.companiesEnabled(), false);
        }
        updateConflicts();
    };

    const handleDeselectPrepayment = () => {
        setValue(RoutingRulesFormFields.prepaidReservationsEnabled(), false);
        updateConflicts();
    };

    const handleAccordionClick = (ruleId: string) => {
        setExpandedAccordions((prevState) => ({
            ...prevState,
            [ruleId]: !prevState[ruleId],
        }));
    };

    return (
        <Stack vertical spacing="300">
            <Banner
                variant="error"
                primaryText={lt(TextKey.RoutingRulesFormConflictsBannerTitle, {
                    count: conflicts.conflictingExistingRules.length,
                })}
                secondaryText={lt(TextKey.RoutingRulesFormConflictsBannerDescription)}
            />
            <Stack vertical spacing="200" data-test-id={DataTestId.RoutingRulesCurrentConflictsSection}>
                <Typography textStyle="titleSmallStrong" as="div">
                    {lt(TextKey.RoutingRulesFormConflictsConfigurationSectionTitle)}
                </Typography>
                <ConflictsList
                    entities={services}
                    title={lt(TextKey.ServicePlural)}
                    handleDeselectEntity={handleDeselectService}
                    handleDeselectAllEntities={handleDeselectAllServices}
                    dataTestId={DataTestId.RoutingRulesConflictsServices}
                />
                <ConflictsList
                    entities={companies}
                    title={lt(TextKey.CompanyPlural)}
                    handleDeselectEntity={handleDeselectCompany(CompaniesFormFields.LinkAsCompany)}
                    handleDeselectAllEntities={() => handleDeselectAllCompanies(CompaniesFormFields.LinkAsCompany)}
                    dataTestId={DataTestId.RoutingRulesConflictsCompanies}
                />
                <ConflictsList
                    entities={travelAgencies}
                    title={lt(TextKey.TravelAgencyPlural)}
                    handleDeselectEntity={handleDeselectCompany(CompaniesFormFields.LinkAsTravelAgency)}
                    handleDeselectAllEntities={() => handleDeselectAllCompanies(CompaniesFormFields.LinkAsTravelAgency)}
                    dataTestId={DataTestId.RoutingRulesConflictsTravelAgencies}
                />
                {rates.map((rate) => (
                    <ConflictsList
                        key={rate.service.id}
                        entities={rate.rates ?? []}
                        title={
                            <>
                                {lt(TextKey.RatePlural)}
                                {UnicodeEntity.NoBreakSpace}
                                {UnicodeEntity.Dash}
                                {UnicodeEntity.NoBreakSpace}
                                {rate.service.name}
                            </>
                        }
                        handleDeselectEntity={handleDeselectRate(rate.service.id)}
                        handleDeselectAllEntities={() => handleDeselectAllRates(rate.service.id)}
                        dataTestId={`${DataTestId.RoutingRulesConflictsRates}-${rate.service.name}`}
                    />
                ))}
                {prepayment === true && (
                    <Stack distribution="space-between" itemAlignment="center">
                        <Typography textStyle="bodyMediumStrong" as="div">
                            {lt(TextKey.RoutingRulesFormConflictsPrepaidLabel)}
                        </Typography>
                        <Button
                            variant="ghost"
                            icon={IconName.CrossClose}
                            onClick={handleDeselectPrepayment}
                            type="submit"
                        >
                            <Typography textStyle="bodyMediumStrong" as="div">
                                {lt(TextKey.Clear)}
                            </Typography>
                        </Button>
                    </Stack>
                )}
            </Stack>
            <Divider />
            <Stack vertical spacing="200" data-test-id={DataTestId.RoutingRulesExistingConflictsSection}>
                <Typography textStyle="titleSmallStrong" as="div">
                    {lt(TextKey.RoutingRulesFormConflictsExistingRulesSectionTitle)}
                </Typography>
                {conflicts.conflictingExistingRules.map((rule) => (
                    <ExistingConflictAccordion
                        key={rule.id}
                        rule={rule}
                        expanded={expandedAccordions[rule.id]}
                        onAccordionClick={handleAccordionClick}
                    />
                ))}
            </Stack>
        </Stack>
    );
};
